import { HGRecordMedia } from "./types";

// Telegram-style photo album layout. Given a set of images (with intrinsic
// width/height) and a container width, produce absolutely-positioned tiles that
// respect each photo's aspect ratio: the signature big-photo / stacked / 2x2
// blocks for small counts, justified rows for larger sets. Ported in spirit from
// tdesktop's grouped-media layout (thresholds below), kept readable per count.

export interface MosaicTile {
  media: HGRecordMedia;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MosaicLayout {
  tiles: MosaicTile[];
  width: number;
  height: number;
}

export interface MosaicOptions {
  maxWidth: number; // measured container width
  maxHeight: number; // vertical cap; layout is scaled down to fit
  spacing: number; // gap between tiles, px
}

// Aspect-ratio classification thresholds (tdesktop).
const WIDE = 1.2;
const NARROW = 0.8;
// Target sum-of-ratios per row for the general (>=5) justified layout. ~2.6
// yields balanced rows of 2-3 landscape or 3-4 portrait photos.
const ROW_TARGET_RATIO = 2.6;

function ratioOf(m: HGRecordMedia): number {
  // Missing intrinsic size (older uploads) -> assume square.
  const w = m.width && m.width > 0 ? m.width : 1;
  const h = m.height && m.height > 0 ? m.height : 1;
  return w / h;
}

function proportion(r: number): "w" | "n" | "q" {
  return r > WIDE ? "w" : r < NARROW ? "n" : "q";
}

export function computeMosaic(
  images: HGRecordMedia[],
  opts: MosaicOptions,
): MosaicLayout {
  const n = images.length;
  if (n === 0) return { tiles: [], width: 0, height: 0 };

  const ratios = images.map(ratioOf);
  if (n === 1) return layoutOne(images[0], ratios[0], opts);

  const props = ratios.map(proportion).join("");
  const avg = ratios.reduce((a, b) => a + b, 0) / n;

  let layout: MosaicLayout;
  if (n === 2) layout = layoutTwo(images, ratios, props, avg, opts);
  else if (n === 3) layout = layoutThree(images, ratios, props, opts);
  else if (n === 4) layout = layoutFour(images, ratios, props, opts);
  else layout = layoutRows(images, ratios, opts);

  return clampHeight(layout, opts.maxHeight);
}

function layoutOne(
  media: HGRecordMedia,
  ratio: number,
  opts: MosaicOptions,
): MosaicLayout {
  let w = opts.maxWidth;
  let h = w / ratio;
  if (h > opts.maxHeight) {
    h = opts.maxHeight;
    w = Math.min(opts.maxWidth, h * ratio);
    h = w / ratio;
  }
  w = Math.round(w);
  h = Math.round(h);
  return {
    tiles: [{ media, left: 0, top: 0, width: w, height: h }],
    width: w,
    height: h,
  };
}

// Lay out `indices` as a single justified row of total width W at a shared
// height. The last tile absorbs rounding so the row fills W exactly.
function layoutRow(
  images: HGRecordMedia[],
  ratios: number[],
  indices: number[],
  top: number,
  W: number,
  spacing: number,
): { tiles: MosaicTile[]; height: number } {
  const k = indices.length;
  const sum = indices.reduce((a, i) => a + ratios[i], 0);
  const avail = W - spacing * (k - 1);
  const h = Math.round(avail / sum);
  const tiles: MosaicTile[] = [];
  let x = 0;
  indices.forEach((idx, j) => {
    const last = j === k - 1;
    const w = last ? W - x : Math.round(ratios[idx] * h);
    tiles.push({ media: images[idx], left: x, top, width: w, height: h });
    x += w + spacing;
  });
  return { tiles, height: h };
}

function layoutTwo(
  images: HGRecordMedia[],
  ratios: number[],
  props: string,
  avg: number,
  opts: MosaicOptions,
): MosaicLayout {
  const { maxWidth: W, spacing } = opts;
  // Two wide landscape photos read best stacked full-width.
  if (props === "ww" && avg > 1.4) {
    const h0 = Math.round(W / ratios[0]);
    const h1 = Math.round(W / ratios[1]);
    return {
      tiles: [
        { media: images[0], left: 0, top: 0, width: W, height: h0 },
        { media: images[1], left: 0, top: h0 + spacing, width: W, height: h1 },
      ],
      width: W,
      height: h0 + spacing + h1,
    };
  }
  // Otherwise side by side, widths proportional to their ratios.
  const row = layoutRow(images, ratios, [0, 1], 0, W, spacing);
  return { tiles: row.tiles, width: W, height: row.height };
}

function layoutThree(
  images: HGRecordMedia[],
  ratios: number[],
  props: string,
  opts: MosaicOptions,
): MosaicLayout {
  const { maxWidth: W, spacing } = opts;
  if (props[0] === "w") {
    // Full-width top + a row of two beneath it.
    const top = layoutRow(images, ratios, [0], 0, W, spacing);
    const bottom = layoutRow(
      images,
      ratios,
      [1, 2],
      top.height + spacing,
      W,
      spacing,
    );
    return {
      tiles: [...top.tiles, ...bottom.tiles],
      width: W,
      height: top.height + spacing + bottom.height,
    };
  }
  // One tall photo on the left, two stacked on the right.
  return leftTallRightColumn(images, ratios, 0, [1, 2], opts);
}

function layoutFour(
  images: HGRecordMedia[],
  ratios: number[],
  props: string,
  opts: MosaicOptions,
): MosaicLayout {
  const { maxWidth: W, spacing } = opts;
  if (props[0] === "w") {
    // Full-width top + a row of three beneath it.
    const top = layoutRow(images, ratios, [0], 0, W, spacing);
    const bottom = layoutRow(
      images,
      ratios,
      [1, 2, 3],
      top.height + spacing,
      W,
      spacing,
    );
    return {
      tiles: [...top.tiles, ...bottom.tiles],
      width: W,
      height: top.height + spacing + bottom.height,
    };
  }
  // One tall photo on the left, three stacked on the right.
  return leftTallRightColumn(images, ratios, 0, [1, 2, 3], opts);
}

// A big photo on the left whose height matches a right-hand column of stacked
// photos. Solve for the split so left + column fill the width exactly and the
// column's total height equals the left photo's height.
function leftTallRightColumn(
  images: HGRecordMedia[],
  ratios: number[],
  leftIdx: number,
  rightIdx: number[],
  opts: MosaicOptions,
): MosaicLayout {
  const { maxWidth: W, spacing } = opts;
  const m = rightIdx.length;
  const gaps = spacing * (m - 1);
  // Column height Hr = wr * K + gaps, where K = sum(1/ratio) of right photos.
  // Left width wl = ratio_left * Hr, and wl + spacing + wr = W. Solve for wr.
  const K = rightIdx.reduce((a, i) => a + 1 / ratios[i], 0);
  const wr = (W - spacing - ratios[leftIdx] * gaps) / (ratios[leftIdx] * K + 1);
  const Hr = wr * K + gaps;
  const wl = W - spacing - wr;
  const wlR = Math.round(wl);
  const HrR = Math.round(Hr);
  const wrR = Math.round(wr);
  const colLeft = wlR + spacing;

  const tiles: MosaicTile[] = [
    { media: images[leftIdx], left: 0, top: 0, width: wlR, height: HrR },
  ];
  let y = 0;
  rightIdx.forEach((idx, j) => {
    const last = j === m - 1;
    const yR = Math.round(y);
    const height = last ? HrR - yR : Math.round(wr / ratios[idx]);
    tiles.push({
      media: images[idx],
      left: colLeft,
      top: yR,
      width: wrR,
      height,
    });
    y += wr / ratios[idx] + spacing;
  });
  return { tiles, width: W, height: HrR };
}

// General case (5+): greedily pack photos into justified rows of 2-4.
function layoutRows(
  images: HGRecordMedia[],
  ratios: number[],
  opts: MosaicOptions,
): MosaicLayout {
  const { maxWidth: W, spacing } = opts;
  const rows: number[][] = [];
  let cur: number[] = [];
  let curSum = 0;
  for (let i = 0; i < images.length; i++) {
    cur.push(i);
    curSum += ratios[i];
    if (cur.length >= 4 || (cur.length >= 2 && curSum >= ROW_TARGET_RATIO)) {
      rows.push(cur);
      cur = [];
      curSum = 0;
    }
  }
  if (cur.length) {
    // Avoid a lonely trailing photo stretched full-width.
    if (cur.length === 1 && rows.length) rows[rows.length - 1].push(cur[0]);
    else rows.push(cur);
  }

  const tiles: MosaicTile[] = [];
  let y = 0;
  for (const row of rows) {
    const r = layoutRow(images, ratios, row, y, W, spacing);
    tiles.push(...r.tiles);
    y += r.height + spacing;
  }
  return { tiles, width: W, height: y - spacing };
}

function clampHeight(layout: MosaicLayout, maxHeight: number): MosaicLayout {
  if (layout.height <= maxHeight) return layout;
  const s = maxHeight / layout.height;
  return {
    width: Math.round(layout.width * s),
    height: Math.round(layout.height * s),
    tiles: layout.tiles.map((t) => ({
      media: t.media,
      left: Math.round(t.left * s),
      top: Math.round(t.top * s),
      width: Math.round(t.width * s),
      height: Math.round(t.height * s),
    })),
  };
}
